import { Component, OnInit } from '@angular/core';
import { UserContextService } from '../../services/user-context.service';
import { SidebarVisibilityService } from '../../services/sidebar-visibility.service';
import { AssociationHelperService } from '../../services/association-helper.service';
import { AuthNavigationService } from '../../services/auth-navigation.service';
import { AssociationOption } from '../../models/association-option.model';

@Component({
  selector: 'app-association-selection',
  templateUrl: './association-selection.component.html',
  styleUrls: ['./association-selection.component.css'],
  standalone: false
})
export class AssociationSelectionComponent implements OnInit {
  associations: AssociationOption[] = [];
  isSelecting = false;
  isLoading = true;
  errorMessage: string | null = null;

  constructor(
    private userContext: UserContextService,
    private authNav: AuthNavigationService,
    private sidebarVisibilityService: SidebarVisibilityService,
    private associationHelper: AssociationHelperService
  ) {}

  async ngOnInit(): Promise<void> {
    // Hide sidebar during association selection
    if (this.sidebarVisibilityService) {
      this.sidebarVisibilityService.showSidebar = false;
    }

    // Ensure user context is initialized
    if (!this.userContext.shouldShowTenantSelection && !this.userContext.sourceId) {
      await this.userContext.initializeAsync();
    }

    if (!this.userContext.shouldShowTenantSelection) {
      setTimeout(() => this.authNav.navigateToHome(), 1);
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    if (this.userContext.isAgVantisUser) {
      // AgVantis: show all available tenants from API
      this.associationHelper.getAssociations().subscribe({
        next: (associations) => {
          this.associations = associations;
          this.isLoading = false;
          if (associations.length === 0) {
            this.errorMessage = 'No associations found. Please ensure at least one association setting is configured.';
          }
        },
        error: (error) => {
          this.associations = [];
          this.isLoading = false;
          const errorDetails = error?.message || error?.toString() || 'Unknown error';
          const errorStatus = error?.status ? ` (Status: ${error.status})` : '';
          const errorUrl = error?.url ? ` URL: ${error.url}` : '';
          this.errorMessage = `Error loading associations: ${errorDetails}${errorStatus}${errorUrl}`;
        }
      });
    } else {
      // Multi-tenant (non-AgVantis): show tenant IDs from claim as options
      const ids = this.userContext.organizationIds;
      this.associations = ids.map(id => ({ sourceId: id, name: id }));
      this.isLoading = false;
      if (this.associations.length === 0) {
        this.errorMessage = 'No tenants available for selection.';
      }
    }
  }

  async selectAssociation(sourceId: string): Promise<void> {
    if (this.isSelecting) {
      return; // Prevent multiple clicks
    }

    try {
      this.isSelecting = true;

      if (!sourceId) {
        this.isSelecting = false;
        return;
      }

      // Set the sourceId in UserContext
      this.userContext.setSourceId(sourceId);

      // Verify the sourceId was set
      if (!this.userContext.sourceId) {
        this.isSelecting = false;
        return;
      }

      // Show sidebar before navigating
      if (this.sidebarVisibilityService) {
        this.sidebarVisibilityService.showSidebar = true;
      }

      // Small delay to show "Loading association..." message
      await new Promise(resolve => setTimeout(resolve, 200));

      // Double-check SourceId is still set before navigating
      if (!this.userContext.sourceId) {
        this.isSelecting = false;
        return;
      }

      this.authNav.navigateToFlaggedContacts();
    } catch (ex) {
      this.isSelecting = false;
    }
  }

}

